/**
 * useLocales composable
 *
 * Source unique des langues proposées par welcome : `static-data/locales.json`
 * du CDN — exactement ce que font les autres apps front (assessments via
 * `api/s3/get.locales`, personal-account via `useLocalesQuery`, phoenix via ses
 * constantes CDN). welcome avait repris de af/auth une liste de codes ISO codée
 * en dur, qui divergeait déjà du CDN (6 langues manquantes) et qui obligeait à
 * reconstruire à la main la clé de traduction de chaque langue, alors que le
 * CDN fournit `language_key` / `localized_language_key`.
 *
 * La liste est chargée une fois au boot (app.ts) puis lue de façon synchrone et
 * réactive par le switcher, le garde du router et l'init i18next.
 */
import { readonly, ref } from 'vue';

import { type CdnLocale, fetchLocales } from '@/services/cdn';

/**
 * CDN injoignable au boot : on ne bloque pas le démarrage pour autant. Les
 * langues embarquées ici sont le strict minimum pour rester utilisable — le
 * switcher affiche donc une liste réduite plutôt que rien.
 */
const FALLBACK_LOCALES: CdnLocale[] = [
  {
    id: 3,
    flag: 'us',
    iso_code: 'en_US',
    iso_code_639: 'en-US',
    label: 'language_american',
    google_id: 'en',
    language_key: 'language.en_us',
    localized_language_key: 'language.localized.en_us'
  },
  {
    id: 1,
    flag: 'fr',
    iso_code: 'fr_FR',
    iso_code_639: 'fr-FR',
    label: 'language_french',
    google_id: 'fr',
    language_key: 'language.fr_fr',
    localized_language_key: 'language.localized.fr_fr'
  }
];

const locales = ref<CdnLocale[]>(FALLBACK_LOCALES);

/** À appeler une fois au boot, avant `initI18N` (qui a besoin de `supportedLngs`). */
export async function loadLocales(): Promise<CdnLocale[]> {
  try {
    const data = await fetchLocales();
    if (data.length) locales.value = data;
  } catch {
    // CDN injoignable — on garde FALLBACK_LOCALES
  }

  return locales.value;
}

export function useLocales() {
  /** Locale supportée la plus proche, à défaut la même langue, sinon `undefined`. */
  function toSupportedLocale(iso: string | undefined): string | undefined {
    if (!iso) return undefined;

    const codes = locales.value.map(l => l.iso_code_639);

    return codes.find(l => l === iso) ?? codes.find(l => l.startsWith(`${iso.split('-')[0]}-`));
  }

  function getLocaleById(id: number | undefined): CdnLocale | undefined {
    return locales.value.find(l => l.id === id);
  }

  function getLocaleByIso(iso: string | undefined): CdnLocale | undefined {
    return locales.value.find(l => l.iso_code_639 === iso);
  }

  return { locales: readonly(locales), toSupportedLocale, getLocaleById, getLocaleByIso };
}
